import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import LeaveBalanceService from '../services/leaveBalanceService';
import LeaveService from '../services/leaveService';
import LeaveApplicationModal from '../components/LeaveApplicationModal';

const MyLeaveBalancesPage = () => {
  const { user } = useAuth();
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchLeaveBalances();
    fetchLeaveHistory();
  }, [selectedYear]);

  const fetchLeaveBalances = async () => {
    try {
      setLoading(true);
      const response = await LeaveBalanceService.getMyLeaveBalances(selectedYear);
      setLeaveBalances(response.data);
    } catch (error) {
      setError('Failed to fetch leave balances');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveHistory = async () => {
    try {
      const response = await LeaveService.getMyLeaves();
      setLeaveHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch leave history:', error);
    }
  };

  const handleApplicationSuccess = () => {
    fetchLeaveBalances();
    fetchLeaveHistory();
  };

  const getBalanceStatus = (balance) => {
    const utilizationRate = (balance.usedLeaves / balance.totalLeaves) * 100;
    if (utilizationRate >= 90) return 'critical';
    if (utilizationRate >= 70) return 'warning';
    return 'good';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { class: 'status-pending', text: 'Pending' },
      APPROVED: { class: 'status-approved', text: 'Approved' },
      REJECTED: { class: 'status-rejected', text: 'Rejected' }
    };
    
    const config = statusConfig[status] || { class: 'status-unknown', text: status };
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  if (user?.role !== 'HR') {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="error-message">
            Access denied. Only HR staff can access this page.
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="page-header">
          <h1>My Leave Balances</h1>
          <div className="header-actions">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="year-selector"
            >
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              className="btn btn-primary"
              onClick={() => setShowApplicationModal(true)}
            >
              Apply for Leave
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} className="btn btn-sm btn-secondary">
              Dismiss
            </button>
          </div>
        )}

        {/* Leave Balances Cards */}
        <div className="leave-balances-section">
          <h2>Leave Balances for {selectedYear}</h2>
          
          {loading ? (
            <div className="loading-message">Loading leave balances...</div>
          ) : leaveBalances.length === 0 ? (
            <div className="empty-state">
              <p>No leave balances assigned for {selectedYear}.</p>
              <p>Please contact HRO to assign leave balances.</p>
            </div>
          ) : (
            <div className="balance-cards-grid">
              {leaveBalances.map((balance) => (
                <div key={balance._id} className={`balance-card ${getBalanceStatus(balance)}`}>
                  <div className="balance-header">
                    <h3>{balance.leaveTypeId.name}</h3>
                    <div
                      className="leave-type-color"
                      style={{ backgroundColor: balance.leaveTypeId.color }}
                    ></div>
                  </div>
                  
                  <div className="balance-stats">
                    <div className="stat-item">
                      <span className="stat-label">Total Allocated</span>
                      <span className="stat-value">{balance.totalLeaves}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Used</span>
                      <span className="stat-value">{balance.usedLeaves}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Remaining</span>
                      <span className="stat-value remaining">{balance.remainingLeaves}</span>
                    </div>
                  </div>

                  <div className="balance-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${(balance.usedLeaves / balance.totalLeaves) * 100}%`
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {Math.round((balance.usedLeaves / balance.totalLeaves) * 100)}% used
                    </span>
                  </div>

                  <div className="balance-info">
                    <p className="leave-type-desc">
                      {balance.leaveTypeId.description || 'No description available'}
                    </p>
                    <p className="balance-meta">
                      Type: {balance.leaveTypeId.isPaid ? 'Paid' : 'Unpaid'} Leave
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Leave History */}
        <div className="leave-history-section">
          <h2>Recent Leave History</h2>
          
          {leaveHistory.length === 0 ? (
            <div className="empty-state">
              <p>No leave history found.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Leave Type</th>
                    <th>Duration</th>
                    <th>Date(s)</th>
                    <th>Days</th>
                    <th>Status</th>
                    <th>Applied On</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveHistory.slice(0, 10).map((leave) => (
                    <tr key={leave._id}>
                      <td>
                        <div className="leave-type-info">
                          <div
                            className="leave-type-indicator"
                            style={{ backgroundColor: leave.leaveTypeId?.color || '#6B7280' }}
                          ></div>
                          {leave.leaveTypeId?.name || 'Unknown'}
                        </div>
                      </td>
                      <td>
                        <span className={`duration-badge ${leave.durationType?.toLowerCase() || 'unknown'}`}>
                          {leave.durationType === 'HALF_DAY' ? 'Half Day' : 
                           leave.durationType === 'FULL_DAY' ? 'Full Day' : 'Unknown'}
                        </span>
                      </td>
                      <td>
                        {formatDate(leave.startDate)}
                        {leave.endDate && leave.endDate !== leave.startDate && (
                          <> - {formatDate(leave.endDate)}</>
                        )}
                      </td>
                      <td>{leave.totalDays || 1} day{(leave.totalDays || 1) > 1 ? 's' : ''}</td>
                      <td>{getStatusBadge(leave.status)}</td>
                      <td>{formatDate(leave.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Leave Application Modal */}
        <LeaveApplicationModal
          isOpen={showApplicationModal}
          onClose={() => setShowApplicationModal(false)}
          onSuccess={handleApplicationSuccess}
        />
      </div>
    </MainLayout>
  );
};

export default MyLeaveBalancesPage;